/**
 * Шаги конструктора образа — единственный источник истины для web.
 *
 * Все шаги фильтруют вещи по кодам категорий, а не по слою: пользователи гораздо чаще
 * проставляют категорию (футболка/куртка), чем атрибут «слой». Коды резолвятся в id через
 * глобальный `/api/refs/categories` (а не filter-options: там только категории, которые уже
 * есть в гардеробе, и при пустой категории фильтр вырождается в «все вещи»).
 *
 * Вынесено из `pages/OutfitBuilderPage.tsx`, чтобы карточка вещи умела определить слот
 * вещи для кнопки «Собрать образ» (CTHS-315), не таща за собой всю страницу.
 * Зеркало iOS — `mobile/ios/ClothesApp/Features/OutfitBuilder/OutfitBuilderModels.swift`.
 */

export type StepKey = 'base' | 'mid' | 'outer' | 'bottom' | 'footwear' | 'accessories';

export interface StepDef {
    key: StepKey;
    label: string;
    description: string;
    multiple: boolean;
    required: boolean;
    categoryCodes: string[];
}

export const STEPS: StepDef[] = [
    {
        key: 'base',
        label: 'Базовый слой',
        description: 'Футболка, майка или рубашка — первый слой',
        multiple: false,
        required: true,
        categoryCodes: ['T_SHIRT', 'SHIRT', 'HOODIE', 'SWEATER', 'CARDIGAN'],
    },
    {
        key: 'mid',
        label: 'Средний слой',
        description: 'Рубашка, свитер или толстовка поверх базового (необязательно)',
        multiple: false,
        required: false,
        categoryCodes: ['SHIRT', 'HOODIE', 'SWEATER', 'CARDIGAN'],
    },
    {
        key: 'outer',
        label: 'Верхний слой',
        description: 'Куртка, пальто или пиджак (необязательно)',
        multiple: false,
        required: false,
        categoryCodes: ['JACKET', 'COAT', 'BLAZER', 'SUIT'],
    },
    {
        key: 'bottom',
        label: 'Низ',
        description: 'Джинсы, брюки, шорты или юбка',
        multiple: false,
        required: true,
        categoryCodes: ['PANTS', 'JEANS', 'SHORTS', 'SKIRT', 'DRESS'],
    },
    {
        key: 'footwear',
        label: 'Обувь',
        description: 'Кроссовки, ботинки, сандалии',
        multiple: false,
        required: true,
        categoryCodes: ['SHOES', 'SNEAKERS', 'BOOTS', 'SANDALS', 'HEELS', 'FLIP_FLOPS'],
    },
    {
        key: 'accessories',
        label: 'Аксессуары',
        description: 'Головные уборы, сумки, украшения, ремни (необязательно)',
        multiple: true,
        required: false,
        categoryCodes: [
            'HAT', 'CAP', 'SCARF', 'GLOVES', 'BELT', 'TIE', 'BOWTIE', 'SOCKS',
            'BAG', 'BACKPACK', 'WALLET', 'WATCH',
            'EARRINGS', 'NECKLACE', 'BRACELET', 'RING', 'SUNGLASSES', 'ACCESSORY',
        ],
    },
];

/** Индекс финального шага «Готово» (сразу за последним слотом). */
export const DONE_STEP = STEPS.length;

/** Соответствие шага конструктора слоту образа на бэкенде (`OutfitSlot`). */
export const STEP_SLOT: Record<StepKey, string> = {
    base: 'BASE', mid: 'MID', outer: 'OUTER', bottom: 'BOTTOM', footwear: 'FOOTWEAR', accessories: 'ACCESSORY',
};

export const SLOT_STEP: Record<string, StepKey> = {
    BASE: 'base', MID: 'mid', OUTER: 'outer', BOTTOM: 'bottom', FOOTWEAR: 'footwear', ACCESSORY: 'accessories',
};

/** Пустой набор выбора по слотам — новый объект на каждый вызов. */
export function emptySelections<T>(): Record<StepKey, T[]> {
    return {base: [], mid: [], outer: [], bottom: [], footwear: [], accessories: []};
}

/**
 * Первый шаг, которому подходит код категории, либо `null`. Рубашка попадает в базовый слой:
 * шаг, объявленный раньше, выигрывает — это и есть «первое свободное место» конструктора.
 */
export function stepIndexForCategoryCode(code: string | null | undefined): number | null {
    if (!code) {
        return null;
    }
    const normalized = code.trim().toUpperCase();
    const index = STEPS.findIndex(step => step.categoryCodes.includes(normalized));
    return index >= 0 ? index : null;
}

/** Слот образа для кода категории (`BASE`, `BOTTOM`, …), либо `null`. */
export function slotForCategoryCode(code: string | null | undefined): string | null {
    const index = stepIndexForCategoryCode(code);
    return index === null ? null : STEP_SLOT[STEPS[index].key];
}
